import { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { AdminHeader } from "@/components/layout/admin-header";
import { AdminSidebar } from "@/components/layout/admin-sidebar";
import { SkipLink } from "@/components/layout/skip-link";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import {
  canAccessAdminPath,
  currentUserIsStaff,
  getDefaultStaffPath,
} from "@/lib/admin";
import { isPublicPath, logout } from "@/lib/auth";
import { useSession } from "@/lib/session-context";

export function AdminLayout({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation("admin");
  const { user, hasSession, sessionReady } = useSession();

  const authorized = useMemo(() => {
    if (isPublicPath(pathname)) return true;
    if (!sessionReady || !hasSession || !user) return false;
    return currentUserIsStaff(user) && canAccessAdminPath(pathname, user);
  }, [pathname, sessionReady, hasSession, user]);

  useEffect(() => {
    if (isPublicPath(pathname) || !sessionReady) return;

    if (!hasSession || !user) {
      navigate("/login", { replace: true });
      return;
    }

    if (!currentUserIsStaff(user)) {
      toast.error(t("guard.notStaff"));
      void logout();
      return;
    }

    if (!canAccessAdminPath(pathname, user)) {
      toast.error(t("guard.notAuthorized"));
      navigate(getDefaultStaffPath(user), { replace: true });
    }
  }, [pathname, navigate, t, sessionReady, hasSession, user]);

  if (isPublicPath(pathname)) {
    return <>{children}</>;
  }

  if (!sessionReady || !authorized) {
    return (
      <div
        className="flex min-h-svh items-center justify-center bg-background"
        role="status"
        aria-live="polite"
      >
        <div className="size-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        <span className="sr-only">{t("a11y.loading")}</span>
      </div>
    );
  }

  return (
    <SidebarProvider>
      <SkipLink />
      <AdminSidebar />
      <SidebarInset id="main-content" aria-label={t("a11y.main")}>
        <AdminHeader />
        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
      </SidebarInset>
    </SidebarProvider>
  );
}
